import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { formatUS } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AnimalSymbol from "@/components/AnimalSymbol";
import AutocompleteInput from "@/components/AutocompleteInput";
import { Plus, Trash2, Library, GitBranchPlus, Search, GitCompare, Download, Upload, LogOut, UserCircle, Shield } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { BUILD_VERSION, BUILD_DATE } from "@/lib/version";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LibraryPage() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [animals, setAnimals] = useState([]);
  const [sort, setSort] = useState("name");
  const [filterSex, setFilterSex] = useState("all");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [form, setForm] = useState({ name: "", sex: "male", species_or_breed: "" });
  const [importOpen, setImportOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);

  const load = async () => {
    const params = { sort };
    if (filterSex !== "all") params.sex = filterSex;
    const r = await axios.get(`${API}/animals`, { params });
    setAnimals(r.data);
  };
  useEffect(() => { load(); }, [sort, filterSex]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return animals;
    return animals.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        (a.species_or_breed || "").toLowerCase().includes(q),
    );
  }, [animals, search]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      const r = await axios.post(`${API}/animals`, form);
      toast.success(`Created ${r.data.name}`);
      setCreateOpen(false);
      setForm({ name: "", sex: "male", species_or_breed: "" });
      nav(`/animals/${r.data.id}`);
    } catch { toast.error("Could not create animal"); }
  };

  const del = async (id) => {
    try {
      await axios.delete(`${API}/animals/${id}`);
      toast.success("Animal deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const runImport = async (dryRun) => {
    if (!importFile) { toast.error("Choose a CSV file first"); return; }
    setImportBusy(true);
    try {
      const form = new FormData();
      form.append("file", importFile);
      const url = `${API}/imports/animals${dryRun ? "?dry_run=true" : ""}`;
      const r = await axios.post(url, form, { headers: { "Content-Type": "multipart/form-data" } });
      if (dryRun) {
        setImportPreview(r.data);
      } else {
        toast.success(`Imported ${r.data.imported} animal${r.data.imported === 1 ? "" : "s"}`);
        setImportOpen(false);
        setImportFile(null);
        setImportPreview(null);
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    }
    setImportBusy(false);
  };

  return (
    <div className="min-h-screen bg-[var(--app-bg)]">
      <Toaster richColors position="top-right" />
      <header className="h-14 border-b border-[var(--app-border)] bg-white px-5 flex items-center gap-3">
        <div className="w-8 h-8 rounded-md bg-[var(--app-bg)] border border-[var(--app-border)] flex items-center justify-center">
          <AnimalSymbol parts={{ ears: "correct", head: "correct", neck: "correct", front: "correct", back: "correct", rear: "correct", tail: "correct" }} size={28} />
        </div>
        <div className="leading-tight whitespace-nowrap">
          <div className="font-display font-extrabold text-[15px] tracking-tight">Vitality Pedigree</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--app-text-soft)]">Structure & Medical</div>
        </div>
        <div className="flex-1" />
        {user && user.is_admin ? (
          <div
            className="text-[10px] uppercase tracking-[0.22em] font-display font-bold px-2 py-1 rounded border border-emerald-300 bg-emerald-50 text-emerald-700"
            data-testid="owner-badge"
            title="Founder / admin access — no trial countdown"
          >
            Owner
          </div>
        ) : user && !user.is_subscribed ? (
          <div
            className={`text-[10px] font-mono-tech px-2 py-1 rounded border ${user.trial_days_left <= 7 ? "border-amber-400 bg-amber-50 text-amber-700" : "border-[var(--app-border)] text-[var(--app-text-soft)]"}`}
            data-testid="trial-badge"
            title={user.trial_days_left > 0 ? "Free trial in progress" : "Trial expired"}
          >
            {user.trial_days_left > 0 ? `${user.trial_days_left} days trial left` : "Trial expired"}
          </div>
        ) : null}
        <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="nav-import-csv">
          <Upload className="w-4 h-4 mr-1.5" /> Import CSV
        </Button>
        <a href={`${API}/exports/animals.csv`} download="vitality-pedigree-library.csv" target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size="sm" type="button" data-testid="nav-export-csv">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </a>
        <Button variant="outline" size="sm" onClick={() => nav("/compare")} data-testid="nav-compare">
          <GitCompare className="w-4 h-4 mr-1.5" /> Compare
        </Button>
        <Button variant="outline" size="sm" onClick={() => nav("/breedings")} data-testid="nav-breedings">
          <GitBranchPlus className="w-4 h-4 mr-1.5" /> Breedings
        </Button>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreateOpen(true)} data-testid="btn-new-animal">
          <Plus className="w-4 h-4 mr-1.5" /> New animal
        </Button>
        <Link to="/account">
          <Button variant="ghost" size="sm" title="Account settings" data-testid="btn-account">
            <UserCircle className="w-4 h-4" />
          </Button>
        </Link>
        {user?.is_admin && (
          <Link to="/admin">
            <Button variant="ghost" size="sm" title="Admin dashboard" data-testid="btn-admin">
              <Shield className="w-4 h-4" />
            </Button>
          </Link>
        )}
        <Button variant="ghost" size="sm" onClick={logout} title={user?.email || "Sign out"} data-testid="btn-logout">
          <LogOut className="w-4 h-4" />
        </Button>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-end gap-3 mb-5 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">Search</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--app-text-soft)]" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or breed/species" className="pl-9" data-testid="search-input" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">Sort by</Label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[220px]" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="species_or_breed">Breed / species</SelectItem>
                <SelectItem value="dob">Date of birth</SelectItem>
                <SelectItem value="updated_at">Recently updated</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">Sex</Label>
            <Select value={filterSex} onValueChange={setFilterSex}>
              <SelectTrigger className="w-[140px]" data-testid="filter-sex"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-[var(--app-border)] rounded-lg">
            <Library className="w-10 h-10 mx-auto mb-3 text-[var(--app-text-soft)]" strokeWidth={1.2} />
            <div className="font-display font-extrabold text-xl mb-1">No animals yet</div>
            <p className="text-sm text-[var(--app-text-soft)] mb-5">Add your first animal to begin recording structure, health and lineage.</p>
            <Button onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="empty-new-animal">
              <Plus className="w-4 h-4 mr-1.5" /> New animal
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="animals-grid">
            {filtered.map((a) => (
              <div key={a.id} className="bg-white border border-[var(--app-border)] rounded-md p-3 flex gap-3 hover:shadow-md transition-shadow" data-testid={`animal-card-${a.id}`}>
                <Link to={`/animals/${a.id}`} className="bg-[var(--app-bg)] rounded p-1 border border-[var(--app-border)] shrink-0">
                  <AnimalSymbol parts={a.parts} size={68} />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/animals/${a.id}`} className="font-display font-extrabold truncate block hover:text-blue-600">{a.name}</Link>
                  <div className="text-xs text-[var(--app-text-soft)] truncate">{a.species_or_breed || "—"}</div>
                  <div className="text-[10px] font-mono-tech text-[var(--app-text-soft)] truncate">{a.sex} {a.dob ? `· ${formatUS(a.dob)}` : ""}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => nav(`/animals/${a.id}`)} data-testid={`open-${a.id}`}>Open</Button>
                    <button type="button" onClick={() => setPendingDelete(a)} className="text-red-500 hover:text-red-700 p-1" data-testid={`delete-${a.id}`} aria-label="Delete">
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Build version footer — lets you verify which deploy is live. */}
        <footer className="mt-12 pt-4 border-t border-[var(--app-border)] flex justify-center">
          <span className="text-[10px] uppercase tracking-[0.22em] font-mono-tech text-[var(--app-text-soft)]" data-testid="build-version">
            Vitality Pedigree · build {BUILD_VERSION} · {BUILD_DATE}
          </span>
        </footer>
      </main>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="create-animal-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold text-2xl">New animal</DialogTitle>
            <DialogDescription className="sr-only">Create a new animal record with name, sex and breed or species.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Animal name" data-testid="new-name" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Sex</Label>
                <Select value={form.sex} onValueChange={(v) => setForm({ ...form, sex: v })}>
                  <SelectTrigger data-testid="new-sex"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Breed or species (optional)</Label>
                <AutocompleteInput field="species_or_breed" value={form.species_or_breed} onChange={(v) => setForm({ ...form, species_or_breed: v })} placeholder="e.g. German Shepherd, Holstein, Quarter Horse" testid="new-species-or-breed" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-create-submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportFile(null); setImportPreview(null); } }}>
        <DialogContent className="sm:max-w-[560px]" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold text-2xl">Import library from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV with at least a <span className="font-mono-tech">name</span> column. Same shape as the Library export.
              Sire / dam columns may reference another row by its <span className="font-mono-tech">id</span> or by <span className="font-mono-tech">name</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="file" accept=".csv" onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportPreview(null); }} data-testid="import-file" />
            {importPreview && (
              <div className="bg-[var(--app-bg)] border border-[var(--app-border)] rounded p-3 text-xs space-y-2" data-testid="import-preview">
                <div>
                  <span className="font-display font-bold">{importPreview.would_import}</span> animal{importPreview.would_import === 1 ? "" : "s"} ready to import.
                </div>
                {importPreview.errors?.length > 0 && (
                  <div className="text-red-600">{importPreview.errors.length} row error{importPreview.errors.length === 1 ? "" : "s"} (see console).</div>
                )}
                {importPreview.unresolved_references?.length > 0 && (
                  <div className="text-amber-700" data-testid="import-unresolved">
                    {importPreview.unresolved_references.length} unresolved sire/dam reference{importPreview.unresolved_references.length === 1 ? "" : "s"} — these will be left blank.
                  </div>
                )}
                <div className="font-mono-tech text-[10px] text-[var(--app-text-soft)] truncate">
                  Preview: {(importPreview.preview || []).slice(0, 5).map((p) => p.name).join(", ")}{(importPreview.preview || []).length > 5 ? "…" : ""}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => runImport(true)} disabled={!importFile || importBusy} data-testid="btn-import-dryrun">
              {importBusy ? "Checking…" : "Preview"}
            </Button>
            <Button type="button" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => runImport(false)} disabled={!importFile || importBusy} data-testid="btn-import-confirm">
              {importBusy ? "Importing…" : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(pendingDelete)} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="confirm-delete-animal">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {pendingDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the animal record. Any other animal that listed it as a parent will have that reference cleared.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={async () => { const a = pendingDelete; setPendingDelete(null); if (a) await del(a.id); }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
